import { SwitchType } from "@components/inputs/switch/Switch";
import { TGetValueFn } from "@components/smart/FieldInfo";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrAbsenceCategoryEntity,
    IPrAbsenceCategoryLegislativeTypeEntity,
    IPrAbsenceCategorySystemTypeEntity,
    PrAbsenceCategoryEntity,
    PrAbsenceCategoryLegislativeTypeEntity,
    PrAbsenceCategorySystemTypeEntity,
    PrAbsenceCategoryTemporalCurrentEntity,
    PrAbsenceCategoryTemporalEntity,
    PrBaseTypeEntity,
    PrVacationEntitlementEntity
} from "@odata/GeneratedEntityTypes";
import { PrAbsenceCompensationCode, PrBaseTypeCode, PrVacationEntitlementInclusionCode } from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import {
    getTemporalAdditionalProperties,
    getTemporalPropertyFieldDefinition,
    TemporalValidityFieldsDef
} from "@odata/TemporalUtils";
import i18next from "i18next";

import { BasicInputSizes, FieldType, Sort } from "../../../enums";
import { Model } from "../../../model/Model";
import { IFormDef } from "../../../views/formView/Form";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import AbsenceCategoryFormView from "./AbsenceCategoryFormView";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import { IFormatOptions } from "@odata/OData.utils";

const isOfType = (...validTypes: PrBaseTypeCode[]): TGetValueFn<boolean> => {
    return (args) => {
        const { entity } = args.storage.data;
        if (!entity) {
            return false;
        }
        const baseType = entity[PrAbsenceCategoryEntity.BaseTypeCode];
        return validTypes.includes(baseType);
    };
};

// Common formatter for Form and Table
const vacationEntitlementFormatter: TFormatterFn = (val, args) => {
    const entity = (args.entity ?? args.storage.data.entity) as IPrAbsenceCategoryEntity;
    const baseType = entity?.BaseTypeCode;
    let type: IPrAbsenceCategoryLegislativeTypeEntity | IPrAbsenceCategorySystemTypeEntity;
    if (PrBaseTypeCode.Legislative === baseType) {
        type = entity.LegislativeType;
    } else if (PrBaseTypeCode.System === baseType) {
        type = entity.SystemType;
    }
    if (type) {
        return type.VacationEntitlementInclusion?.Name;
    }
    return getEnumDisplayValue(EntityTypeName.PrVacationEntitlementInclusion, val as string);
};

const compensationTableFormatter: TFormatterFn = (val, args: IFormatOptions<IPrAbsenceCategoryEntity>) => {
    const { entity } = args;
    const baseType = entity?.BaseTypeCode;
    if (PrBaseTypeCode.Legislative === baseType) {
        switch (entity.LegislativeType.CompensationCode) {
            case PrAbsenceCompensationCode.Paid:
                val = 100;
                break;
            case PrAbsenceCompensationCode.Unpaid:
                val = 0;
                break;
            case PrAbsenceCompensationCode.Special:
                return i18next.t("AbsenceCategory:Table.SpecialCompensation").toString();
        }
    }
    return `${val} ${args.unit} ${val ? i18next.t("AbsenceCategory:Table.AverageWageCompensation") : ""}`;
};

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrAbsenceCategory}Table`,
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name),
                PrAbsenceCategoryEntity.BaseType,
                PrAbsenceCategoryEntity.Description,
            ],
            filterDefinition: {
                [createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name)]: {},
                [PrAbsenceCategoryEntity.BaseType]: {
                    fieldSettings: {
                        displayName: PrBaseTypeEntity.Name
                    }
                },
                [PrAbsenceCategoryEntity.Description]: {},
                // [createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.CompensationRate)]: {
                //     formatter: (val, args) => {
                //         return isDefined(val) ? `${val} %` : args.placeholder;
                //     }
                // }
            },
            isValueHelp: true
        }],
        initialSortBy: [{
            id: createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name),
            sort: Sort.Desc
        }],
        additionalProperties: [
            { id: PrAbsenceCategoryEntity.BaseTypeCode },
            {
                id: PrAbsenceCategoryEntity.LegislativeType,
                additionalProperties: [
                    { id: PrAbsenceCategoryLegislativeTypeEntity.CompensationCode },
                    { id: createPath(PrAbsenceCategoryLegislativeTypeEntity.VacationEntitlementInclusion, PrVacationEntitlementEntity.Name) }
                ]
            },
            {
                id: PrAbsenceCategoryEntity.SystemType,
                additionalProperties: [
                    { id: createPath(PrAbsenceCategorySystemTypeEntity.VacationEntitlementInclusion, PrVacationEntitlementEntity.Name) }
                ]
            }
        ],
        columns: [
            createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name),
            PrAbsenceCategoryEntity.BaseType,
            createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.CompensationRate),
            createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.VacationEntitlementInclusion),
            PrAbsenceCategoryEntity.Description,
        ],
        columnDefinition: {
            [createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name)]: {},
            [createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.CompensationRate)]: {
                fieldSettings: {
                    unit: "%",
                },
                formatter: compensationTableFormatter,
                disableSort: true
            },
            [PrAbsenceCategoryEntity.BaseType]: {
                fieldSettings: {
                    displayName: PrBaseTypeEntity.Name
                }
            },
            [createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.VacationEntitlementInclusion)]: {
                label: i18next.t("AbsenceCategory:Table.VacationEntitlementInclusion"),
                fieldSettings: {
                    displayName: PrVacationEntitlementEntity.Name
                },
                formatter: vacationEntitlementFormatter,
                disableSort: true
            },
            [PrAbsenceCategoryEntity.Description]: {}
        },
        title: i18next.t("AbsenceCategory:Title"),
        tabsSettings: {}
    };
    const summary: ISummaryItem[] = [];
    const getTemporalDialogSettings = (fieldId: PrAbsenceCategoryTemporalCurrentEntity) => ({
        dialogTitle: i18next.t("AbsenceCategory:Form.PlanAbsenceCategoryTitle"),
        columns: [
            fieldId
        ]
    });

    const form: IFormDef = {
        id: `${EntityTypeName.PrAbsenceCategory}Form`,
        translationFiles: getDefinitions.translationFiles,
        getItemBreadCrumbText: (storage: Model) =>
                getItemBreadCrumbsText(storage, i18next.t("AbsenceCategory:New"), storage.data.entity?.CurrentTemporalPropertyBag?.Name),
        isDeletable: true,
        formControl: AbsenceCategoryFormView,
        summary,
        additionalProperties: [
            ...getTemporalAdditionalProperties(PrAbsenceCategoryTemporalEntity, PrAbsenceCategoryEntity),
            { id: PrAbsenceCategoryEntity.BaseTypeCode },
            {
                id: PrAbsenceCategoryEntity.LegislativeType,
                additionalProperties: [
                    { id: createPath(PrAbsenceCategoryLegislativeTypeEntity.VacationEntitlementInclusion, PrVacationEntitlementEntity.Name) }
                ]
            },
            {
                id: PrAbsenceCategoryEntity.SystemType,
                additionalProperties: [
                    { id: createPath(PrAbsenceCategorySystemTypeEntity.VacationEntitlementInclusion, PrVacationEntitlementEntity.Name) }
                ]
            }
        ],
        fieldDefinition: {
            ...getTemporalPropertyFieldDefinition({
                propName: PrAbsenceCategoryTemporalEntity.Name,
                definition: {
                    width: BasicInputSizes.M,
                    isReadOnly: isOfType(PrBaseTypeCode.Legislative, PrBaseTypeCode.System),
                    fieldSettings: {
                        temporalDialog: getTemporalDialogSettings(PrAbsenceCategoryTemporalCurrentEntity.Name),
                    }
                },
                withOpeningButton: false
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrAbsenceCategoryTemporalCurrentEntity.CompensationRate,
                definition: {
                    width: BasicInputSizes.S,
                    isVisible: isOfType(PrBaseTypeCode.Custom, PrBaseTypeCode.System),
                    isRequired: true,
                    fieldSettings: {
                        temporalDialog: getTemporalDialogSettings(PrAbsenceCategoryTemporalCurrentEntity.CompensationRate),
                        unit: "%"
                    }
                },
                withOpeningButton: true
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrAbsenceCategoryTemporalCurrentEntity.VacationEntitlementInclusion,
                definition: {
                    type: FieldType.ComboBox,
                    width: BasicInputSizes.XL,
                    isReadOnly: isOfType(PrBaseTypeCode.Legislative, PrBaseTypeCode.System),
                    isRequired: isOfType(PrBaseTypeCode.Custom),
                    defaultValue: PrVacationEntitlementInclusionCode.Full,
                    fieldSettings: {
                        displayName: PrVacationEntitlementEntity.Name,
                        temporalDialog: getTemporalDialogSettings(PrAbsenceCategoryTemporalCurrentEntity.VacationEntitlementInclusion),
                    },
                    formatter: vacationEntitlementFormatter
                },
                withOpeningButton: true
            }),
            [PrAbsenceCategoryEntity.BaseType]: {
                type: FieldType.ComboBox,
                defaultValue: PrBaseTypeCode.Custom,
                isReadOnly: true,
                fieldSettings: {
                    displayName: PrBaseTypeEntity.Name
                }
            },
            [PrAbsenceCategoryEntity.Description]: {
                width: BasicInputSizes.XL
            },
            [PrAbsenceCategoryEntity.IsActive]: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.Icons
                },
                defaultValue: true
            },
            [PrAbsenceCategoryEntity.Color]: {
                type: FieldType.ColorPicker,
                width: BasicInputSizes.fitContent,
                defaultValue: "C_CHART_blue",
                fieldSettings: {
                    colors: [
                        "C_CHART_blue",
                        "C_CHART_green",
                        "C_CHART_yellow",
                        "C_CHART_red",
                        "C_CHART_orange",
                        "C_CHART_grey"
                    ]
                }
            },
            ...TemporalValidityFieldsDef
        },
        groups: [
            {
                id: "main",
                rows: [
                    [
                        { id: createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.Name) },
                        { id: PrAbsenceCategoryEntity.BaseType },
                        { id: PrAbsenceCategoryEntity.Color },
                        { id: createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.CompensationRate) },
                        { id: createPath(PrAbsenceCategoryEntity.CurrentTemporalPropertyBag, PrAbsenceCategoryTemporalCurrentEntity.VacationEntitlementInclusion) }
                    ],
                        [
                            { id: PrAbsenceCategoryEntity.Description },
                            { id: PrAbsenceCategoryEntity.IsActive },
                        ]
                ]
            }
        ]
    };
    return {
        entitySet: EntitySetName.PrAbsenceCategories,
        table,
        form
    };
};

getDefinitions.translationFiles = ["AbsenceCategory", "Common"];
setDefByEntityType(EntityTypeName.PrAbsenceCategory, getDefinitions);